import React, { useRef } from 'react';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-sql';

/**
 * SQL editor with syntax highlighting and keyboard shortcuts
 * Supports: Tab (indent), Shift-Tab (outdent), Cmd/Ctrl-Enter (execute), Cmd/Ctrl-/ (toggle comment)
 * Props:
 *   - value: string
 *   - onChange: (value: string) => void
 *   - onExecute: () => void
 *   - placeholder: string (optional)
 *   - disabled: boolean (optional)
 *   - verdict: 'pass' | 'fail' | 'close' | null (optional)
 *   - error: string | null (optional)
 */
const SQLEditor = ({ value, onChange, onExecute, placeholder, disabled = false, verdict = null, error = null }) => {
  const editorRef = useRef(null);

  const handleKeyDown = (e) => {
    // Cmd/Ctrl-Enter: execute query
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (onExecute && !disabled) {
        onExecute();
      }
      return;
    }

    // Cmd/Ctrl-/: toggle comment on current line
    if (e.key === '/' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      const textarea = e.target;
      const { selectionStart, selectionEnd } = textarea;
      toggleComment(textarea, value, selectionStart, selectionEnd);
      return;
    }

    // Page Down: move cursor down 10 lines
    if (e.key === 'PageDown') {
      e.preventDefault();
      moveCursorByLines(e.target, 10);
      return;
    }

    // Page Up: move cursor up 10 lines
    if (e.key === 'PageUp') {
      e.preventDefault();
      moveCursorByLines(e.target, -10);
      return;
    }
  };

  const moveCursorByLines = (textarea, lineCount) => {
    const lines = value.split('\n');
    const { selectionStart } = textarea;

    // Find current line and column
    let charCount = 0;
    let currentLine = 0;
    let currentColumn = 0;

    for (let i = 0; i < lines.length; i++) {
      const lineLength = lines[i].length;
      if (charCount + lineLength >= selectionStart) {
        currentLine = i;
        currentColumn = selectionStart - charCount;
        break;
      }
      charCount += lineLength + 1; // +1 for newline
    }

    // Calculate target line
    const targetLine = Math.max(0, Math.min(lines.length - 1, currentLine + lineCount));

    // Calculate new position
    let newPos = 0;
    for (let i = 0; i < targetLine; i++) {
      newPos += lines[i].length + 1;
    }

    // Try to preserve column position
    const targetColumn = Math.min(currentColumn, lines[targetLine].length);
    newPos += targetColumn;

    // Update cursor
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = newPos;
    }, 0);
  };

  const toggleComment = (textarea, currentValue, selectionStart, selectionEnd) => {
    // Find the start and end of the current line(s)
    const lines = currentValue.split('\n');
    let charCount = 0;
    let startLineIdx = 0;
    let endLineIdx = 0;

    // Find which line(s) the selection spans
    for (let i = 0; i < lines.length; i++) {
      const lineLength = lines[i].length + 1; // +1 for newline
      if (charCount <= selectionStart && selectionStart < charCount + lineLength) {
        startLineIdx = i;
      }
      if (charCount <= selectionEnd && selectionEnd <= charCount + lineLength) {
        endLineIdx = i;
      }
      charCount += lineLength;
    }

    // Check if all selected lines are commented
    const selectedLines = lines.slice(startLineIdx, endLineIdx + 1);
    const allCommented = selectedLines.every(line => {
      const trimmed = line.trim();
      return trimmed === '' || trimmed.startsWith('-- ') || trimmed.startsWith('--');
    });

    // Toggle comments
    const newLines = [...lines];
    for (let i = startLineIdx; i <= endLineIdx; i++) {
      if (allCommented) {
        // Uncomment: remove "-- " or "--"
        newLines[i] = newLines[i].replace(/^(\s*)--\s?/, '$1');
      } else {
        // Comment: add "-- " at the start (after leading whitespace)
        const match = newLines[i].match(/^(\s*)/);
        const leadingWhitespace = match ? match[1] : '';
        const rest = newLines[i].substring(leadingWhitespace.length);
        if (rest.length > 0) {
          newLines[i] = leadingWhitespace + '-- ' + rest;
        }
      }
    }

    const newValue = newLines.join('\n');
    onChange(newValue);
  };

  const highlightSql = (code) => {
    return highlight(code, languages.sql, 'sql');
  };

  // Determine container class based on verdict or error
  let containerClass = 'sql-editor-container';
  if (verdict === 'pass') {
    containerClass += ' success';
  } else if (verdict === 'fail' || error) {
    containerClass += ' error';
  } else if (verdict === 'close') {
    containerClass += ' warning';
  }

  return (
    <div className={containerClass}>
      <div className="sql-editor-header">
        <span className="sql-editor-label">SQL Query</span>
        <span className="sql-editor-hint">
          Tab · {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}↵ Execute · {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}/ Comment
        </span>
      </div>
      <div className="sql-editor-wrapper">
        <Editor
          ref={editorRef}
          value={value}
          onValueChange={onChange}
          highlight={highlightSql}
          padding={16}
          tabSize={2}
          insertSpaces={true}
          placeholder={placeholder || '-- Your SQL query here'}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          className="sql-editor"
          style={{
            fontFamily: 'var(--mono-font), "Courier New", monospace',
            fontSize: '0.95rem',
            lineHeight: 1.6
          }}
        />
      </div>
    </div>
  );
};

export default SQLEditor;
